import type { Api_Video, Api_Video_Poster } from '@core/api'
import { orderVideoFiles } from '@core/video/videoElementWrapper/utils/video'

import type { CDNOptionsType } from './roundRobinCDN'
import { roundRobinCDN } from './roundRobinCDN'

export { roundRobinCDN }

/**
 * Returns first jpeg poster from `video_posters`. Item typically provided
 * by user as a custom poster. Other auto-generated posters are not jpeg (gif and webp).
 */
export function getVideoPosterJpg(video?: Api_Video | null) {
  return video?.video_posters?.find((poster) => poster.format === 'jpg')?.url
}

/**
 * Returns the placeholder's blurhash for the first suitable video file from `video_files`.
 */
export function getVideoBlurhash(video?: Api_Video | null) {
  const orderedVideoFiles = video ? orderVideoFiles(video, []) : []
  return orderedVideoFiles[0]?.placeholder_blur_hash || ''
}

/**
 * Returns the placeholder url for the first suitable video file from `video_files`. Note
 * the placeholder is a first frame of the video.
 */
export function getVideoPlaceholder(
  video?: Api_Video | null,
  preferredCDNs?: CDNOptionsType[],
) {
  const orderedVideoFiles = video ? orderVideoFiles(video, preferredCDNs) : []
  return orderedVideoFiles[0]?.placeholder_url || ''
}

/**
 * Returns first `jpg` poster from `video_posters` or `thumbnail_url`
 * if none found. This helper is legacy and does not take placeholder into
 * account.
 */
export function getVideoPoster(
  video?: Api_Video | null,
  preferredCDNs?: CDNOptionsType[],
) {
  return roundRobinCDN(
    getVideoPosterJpg(video) || video?.thumbnail_url || '',
    preferredCDNs,
  )
}

/**
 * Gets a video's poster by type
 * @param video a video with video_posters
 * @param type the type to search for
 * @param format the image format to search for
 */
export const getVideoPosterByType = (
  video: Api_Video,
  type: Api_Video_Poster['video_poster_type'],
  format?: Api_Video_Poster['format'],
): Api_Video_Poster | undefined =>
  video?.video_posters.find(
    (poster) =>
      poster.video_poster_type === type &&
      (format ? poster.format === format : true),
  )
